import { CancelButton } from '@/features/import_export_histories/Table/Columns/Cells/components/CancelButton';
import { DetailsButton } from '@/features/import_export_histories/Table/Columns/Cells/components/DetailsButton';
import { DownloadButton } from '@/features/import_export_histories/Table/Columns/Cells/components/DownloadButton';
import { useStatusLabel } from '@/features/import_export_histories/Table/hooks/useStatusLabel';
import {
  ImportExportHistoriesColumn,
  OnDownloadClick,
} from '@/features/import_export_histories/type';
import { useTranslation } from '@/i18n';
import {
  Caption,
  IconApproval,
  IconCancel,
  IconError,
  IconPending,
} from '@moneyforward/ap-frontend-components';
import cx from 'classnames';
import { FC, memo, useCallback } from 'react';
import styles from '../column.module.scss';

type Props = {
  status: ImportExportHistoriesColumn['status'];
  canCancel?: boolean;
  onCancelClick?: () => Promise<void>;
  onDetailsClick?: () => void;
  type?: ImportExportHistoriesColumn['type'];
  downloadFileId?: string;
  downloadFileName?: string;
  canDownload?: boolean;
  onDownloadClick?: OnDownloadClick;
  errorMessage?: string | null;
};

const CAPTION_TYPE_MAP = {
  processing: 'success',
  failed: 'error',
  completed: 'helper',
  canceled: 'helper',
} as const;

const getCaptionType = (status: ImportExportHistoriesColumn['status']) =>
  CAPTION_TYPE_MAP[status];

const getIconComponent = (status: ImportExportHistoriesColumn['status']) => {
  switch (status) {
    case 'processing':
      return IconPending;
    case 'failed':
      return IconError;
    case 'completed':
      return IconApproval;
    case 'canceled':
      return IconCancel;
    default:
      return null;
  }
};

export const StatusCell: FC<Props> = memo(
  ({
    status,
    canCancel,
    onCancelClick,
    onDetailsClick,
    type,
    downloadFileId,
    downloadFileName,
    canDownload,
    onDownloadClick,
    errorMessage,
  }) => {
    const { t } = useTranslation();
    const label = useStatusLabel(status);
    const captionType = getCaptionType(status);
    const IconComponent = getIconComponent(status);

    const showExportDownload = type === 'export' && status === 'completed';
    const isTypeImport = type === 'import';
    const isStatusFailedOrCanceled =
      status === 'failed' || status === 'canceled';

    const handleDownloadClick = useCallback(async () => {
      if (downloadFileId !== undefined && onDownloadClick) {
        await onDownloadClick(downloadFileId, downloadFileName);
      }
    }, [downloadFileId, downloadFileName, onDownloadClick]);

    const showDetailsButton =
      isTypeImport && isStatusFailedOrCanceled && onDetailsClick;
    const showErrorMessage = isStatusFailedOrCanceled && !!errorMessage;
    const showCancelButton =
      status === 'processing' && isTypeImport && canCancel && onCancelClick;

    return (
      <>
        <div className={styles['status-cell']}>
          <div className={styles['status-wrapper']}>
            <Caption
              type={captionType}
              icon={false}
              data-testid={`caption-${captionType}`}
            >
              <div className={styles['status-cell-content']}>
                {IconComponent && (
                  <div className={styles['status-icon']}>
                    <IconComponent
                      width={16}
                      height={16}
                      className={styles[`status-icon-${captionType}`]}
                    />
                  </div>
                )}
                <span
                  className={cx(
                    styles['status-label'],
                    styles[`status-label-${captionType}`]
                  )}
                >
                  {label}
                </span>
              </div>
            </Caption>
          </div>
          {showExportDownload && (
            <DownloadButton
              onClick={handleDownloadClick}
              isDisabled={!canDownload}
              tooltipTitle={
                !canDownload
                  ? t('import_export_history_download_expired')
                  : undefined
              }
              variant='text'
            />
          )}
          {showCancelButton && <CancelButton onClick={onCancelClick} />}
          {showDetailsButton && <DetailsButton onClick={onDetailsClick} />}
        </div>
        {showErrorMessage && (
          <div className={styles['status-error-message']}>{errorMessage}</div>
        )}
      </>
    );
  }
);

StatusCell.displayName = 'StatusCell';
